<script lang="ts">
import { defineComponent, onMounted } from 'vue'
import ButtonLpCta from '@/components/button/ButtonLpCta.vue'
import bgAbout01 from '@/images/about/bg-about_01.jpg'
import { newUserRegistrationPath } from '@/generated/railsRoutes'

export default defineComponent({
  name: 'AboutMain',

  components: {
    ButtonLpCta,
  },

  props: {
    ctaButtonLabel: {
      type: String,
      default: '',
    },

    trackingCodePrefix: {
      type: String,
      default: '',
    },
  },

  emits: ['mounted:sections'],

  setup(_, { emit }) {
    const SECTION_IDS = [
      'about-01',
      'about-02',
      'about-03',
      'about-04',
    ] as const

    onMounted(() => {
      emit('mounted:sections', SECTION_IDS)
    })

    return {
      // constants
      SECTION_IDS,

      // routes
      newUserRegistrationPath,

      // images
      bgAbout01,
    }
  },
})
</script>

<template>
  <div class="div">
    <section
      :id="SECTION_IDS[0]"
      class="f-section__about"
      data-point="1">
      <div class="f-section__about--body is-background">
        <div class="o-figure__background">
          <img
            class="o-figure__background--item"
            :src="bgAbout01"
            loading="lazy"
            alt="">
        <!-- /.o-figure__background --></div>
      <!-- /.f-section__about body --></div>

      <div class="f-section__about--body is-contents">
        <div class="o-heading__section">
          <h2 class="o-heading__section--item">
            経営者の「探す時間」を削減し<span class="span u-text__block">
              意思決定を速くする
            </span>
          </h2>
        <!-- /.o-heading__section --></div>

        <p class="p u-text__center u-text__x-large">
          経営の課題は待ってくれません。<br>
          人材、法務、IT、資金繰り…
        </p>
        <p class="p u-text__center u-text__x-large">
          解決策を探す時間を短縮し、経営判断のスピードと質を高めるために。<br>
          BizHint(ビズヒント)は、20万人の経営者・役職者が利用する「経営情報プラットフォーム」です。
        </p>
        <p class="p u-text__center u-text__x-large">
          最新の取材記事、SaaSツール情報、実務に使える資料やテンプレートをまとめて提供し、<span class="span u-text__block">
            経営者の「次の一手」を後押しします。
          </span>
        </p>
      <!-- /.f-section__about body --></div>
    <!-- /.f-section__about --></section>

    <section
      :id="SECTION_IDS[1]"
      class="f-section__about"
      data-point="2">
      <div class="f-section__about--body is-contents">
        <div class="o-heading__section">
          <h2 class="o-heading__section--item">
            多くの経営者に選ばれています
          </h2>
        <!-- /.o-heading__section --></div>

        <div class="o-list__achievement">
          <dl class="o-list__achievement--unit">
            <dt class="o-list__achievement--term">
              登録会員数<span class="span u-text__x-small">
                (累計)
              </span>
            </dt>

            <dd class="o-list__achievement--description">
              <div class="o-label__achievement">
                <p class="o-label__achievement--item is-primary">
                  <strong class="strong">70万</strong>人以上
                </p>
                <p class="o-label__achievement--item is-secondary">
                  中小企業から大手まで幅広い層が活用
                </p>
              <!-- /.o-label__achievement --></div>
            </dd>
          </dl>

          <dl class="o-list__achievement--unit">
            <dt class="o-list__achievement--term">
              経営者・役員クラス
            </dt>

            <dd class="o-list__achievement--description">
              <div class="o-label__achievement">
                <p class="o-label__achievement--item is-primary">
                  <strong class="strong">20万</strong>人以上
                </p>
                <p class="o-label__achievement--item is-secondary">
                  会員の約30%が経営層
                </p>
              <!-- /.o-label__achievement --></div>
            </dd>
          </dl>

          <dl class="o-list__achievement--unit">
            <dt class="o-list__achievement--term">
              業界
            </dt>

            <dd class="o-list__achievement--description">
              <div class="o-label__achievement">
                <p class="o-label__achievement--item is-primary">
                  <strong class="strong">17</strong>業界
                </p>
                <p class="o-label__achievement--item is-secondary">
                  製造・IT・流通・建設など
                </p>
              <!-- /.o-label__achievement --></div>
            </dd>
          </dl>
        <!-- /.o-list__achievement --></div>
      <!-- /.f-section__about body --></div>
    <!-- /.f-section__about --></section>

    <section
      :id="SECTION_IDS[2]"
      class="f-section__about"
      data-point="3">
      <div class="f-section__about--body is-contents">
        <div class="o-heading__section">
          <h2 class="o-heading__section--item">
            BizHintで手に入る、4つの情報
          </h2>
        <!-- /.o-heading__section --></div>

        <div class="l-cols__feature">
          <div class="l-cols__feature--col">
            <div class="l-unit__feature">
              <div class="l-unit__feature--header">
                <div class="o-heading__feature">
                  <h3 class="o-heading__feature--item is-primary">
                    経営者への取材記事
                  </h3>
                  <p class="o-heading__feature--item is-secondary">
                    トップ本人が語る、意思決定の裏側
                  </p>
                <!-- /.o-heading__feature --></div>
              <!-- /.l-unit__feature header --></div>

              <div class="l-unit__feature--image">
                <img
                  class="img"
                  src="@/images/about/pic-feature_01.svg"
                  loading="lazy"
                  alt="">
              <!-- /.l-unit__feature image --></div>

              <div class="l-unit__feature--description">
                <p class="p">
                  編集部がこれまで700社以上の経営者・社長に直接取材。<br>
                  成功だけでなく、試行錯誤の過程や判断の根拠も語られており、教科書にはない、トップだけが持つ生の知見があります。
                </p>
              <!-- /.l-unit__feature description --></div>
            <!-- /.l-unit__feature --></div>
          <!-- /.l-cols__feature col --></div>

          <div class="l-cols__feature--col">
            <div class="l-unit__feature">
              <div class="l-unit__feature--header">
                <div class="o-heading__feature">
                  <h3 class="o-heading__feature--item is-primary">
                    AIが代わりに探す、経営者向けニュースレター
                  </h3>
                  <p class="o-heading__feature--item is-secondary">
                    あなたに必要な情報が、定期的に届く
                  </p>
                <!-- /.o-heading__feature --></div>
              <!-- /.l-unit__feature header --></div>

              <div class="l-unit__feature--image">
                <img
                  class="img"
                  src="@/images/about/pic-feature_02.svg"
                  loading="lazy"
                  alt="">
              <!-- /.l-unit__feature image --></div>

              <div class="l-unit__feature--description">
                <p class="p">
                  AIがあなたの関心や課題に合わせて情報を選定。<br>
                  SaaSツール、実践事例、実務ノウハウがメールで定期的に届き、あなたの探す手間はゼロです。
                </p>
              <!-- /.l-unit__feature description --></div>
            <!-- /.l-unit__feature --></div>
          <!-- /.l-cols__feature col --></div>

          <div class="l-cols__feature--col">
            <div class="l-unit__feature">
              <div class="l-unit__feature--header">
                <div class="o-heading__feature">
                  <h3 class="o-heading__feature--item is-primary">
                    実務で使える資料・ノウハウ
                  </h3>
                  <p class="o-heading__feature--item is-secondary">
                    組織・人事・法律解説まで、すぐ役立つ実務知識
                  </p>
                <!-- /.o-heading__feature --></div>
              <!-- /.l-unit__feature header --></div>

              <div class="l-unit__feature--image">
                <img
                  class="img"
                  src="@/images/about/pic-feature_03.svg"
                  loading="lazy"
                  alt="">
              <!-- /.l-unit__feature image --></div>

              <div class="l-unit__feature--description">
                <p class="p">
                  組織マネジメント、人事制度、法律解説など、経営に必要な実務知識をホワイトペーパーで提供。テンプレートやチェックリストも揃っており、すぐ実務で活用できます。
                </p>
              <!-- /.l-unit__feature description --></div>
            <!-- /.l-unit__feature --></div>
          <!-- /.l-cols__feature col --></div>

          <div class="l-cols__feature--col">
            <div class="l-unit__feature">
              <div class="l-unit__feature--header">
                <div class="o-heading__feature">
                  <h3 class="o-heading__feature--item is-primary">
                    会員同士の気づき
                  </h3>
                  <p class="o-heading__feature--item is-secondary">
                    他の経営者の視点も知ることができる
                  </p>
                <!-- /.o-heading__feature --></div>
              <!-- /.l-unit__feature header --></div>

              <div class="l-unit__feature--image">
                <img
                  class="img"
                  src="@/images/about/pic-feature_04.svg"
                  loading="lazy"
                  alt="">
              <!-- /.l-unit__feature image --></div>

              <div class="l-unit__feature--description">
                <p class="p">
                  記事にはコメント機能があり、他の経営者の感想や気づきを確認できます。 同じ課題に向き合うリーダーたちの、率直な声が集まる場所です。
                </p>
              <!-- /.l-unit__feature description --></div>
            <!-- /.l-unit__feature --></div>
          <!-- /.l-cols__feature col --></div>
        <!-- /.l-cols__feature --></div>

        <ButtonLpCta
          class="u-block__max560"
          :href="newUserRegistrationPath({ trcd: `${trackingCodePrefix}_1` })"
          font-size="xLarge"
          size="normal"
          animate>
          <span
            class="span"
            v-html="ctaButtonLabel" />
        </ButtonLpCta>
      <!-- /.f-section__about body --></div>
    <!-- /.f-section__about --></section>

    <section
      :id="SECTION_IDS[3]"
      class="f-section__about"
      data-point="4">
      <div class="f-section__about--body is-contents">
        <div class="o-heading__section">
          <h2 class="o-heading__section--item">
            利用している経営者たちの声
          </h2>
        <!-- /.o-heading__section --></div>

        <div class="o-list__feedback">
          <ul class="o-list__feedback--items">
            <li class="o-list__feedback--item">
              <div class="l-unit__feedback">
                <div class="l-unit__feedback--header">
                  <div class="o-label__profile">
                    <p class="o-label__profile--item">
                      コンサルティング（人事評価・経営/人事）業 / 代表取締役
                    </p>
                  <!-- /.o-label__profile --></div>
                <!-- /.l-unit__feedback header --></div>

                <div class="l-unit__feedback--image">
                  <img
                    class="img"
                    src="@/images/about/pic-feedback_01.svg"
                    loading="lazy"
                    alt="">
                <!-- /.l-unit__feedback image --></div>

                <div class="l-unit__feedback--description">
                  <p class="p">
                    中小企業家同友会の経営指針作成部会で、経営理念の重要性に気付き、パッチワークの経営理念からご自分の言葉で経営理念を綴られるようになったことに留まらず、会社に浸透させていった<em class="em">情熱と行動力に、中小企業経営者のお手本を観させてもらったように思います。</em>
                  </p>
                  <p class="p">
                    先輩方は口が悪くても心に火をつけることが上手なんだな（笑）と共感しながら拝読させていただきました。貴重な体験報告ありがとうございました。
                  </p>
                <!-- /.l-unit__feedback description --></div>

                <div class="l-unit__feedback--footer">
                  <div class="o-label__source">
                    <cite class="o-label__source--item">
                      「経営者がバカだと社員が不幸」覚醒からのV字成長。松下幸之助“指導者の条件”がバイブル より
                    </cite>
                  <!-- /.o-label__source --></div>
                <!-- /.l-unit__feedback footer --></div>
              <!-- /.l-unit__feedback --></div>
            </li>

            <li class="o-list__feedback--item">
              <div class="l-unit__feedback">
                <div class="l-unit__feedback--header">
                  <div class="o-label__profile">
                    <p class="o-label__profile--item">
                      一般貨物輸送・倉庫管理業 / 会社顧問
                    </p>
                  <!-- /.o-label__profile --></div>
                <!-- /.l-unit__feedback header --></div>

                <div class="l-unit__feedback--image">
                  <img
                    class="img"
                    src="@/images/about/pic-feedback_02.svg"
                    loading="lazy"
                    alt="">
                <!-- /.l-unit__feedback image --></div>

                <div class="l-unit__feedback--description">
                  <p class="p">
                    <em class="em">『現地現物現実』『発想力は移動距離に比例する』『属人化解消』『起業家精神』『熱い想い』など、感銘を受ける言葉がたくさんありました。</em>私も社長業3年目に入り、『誰の為に経営しているの？』という問いから、従業員ファースト＝頑張った人が報われる会社にという想いで、MVVを明確にして経営しています。
                  </p>
                <!-- /.l-unit__feedback description --></div>

                <div class="l-unit__feedback--footer">
                  <div class="o-label__source">
                    <cite class="o-label__source--item">
                      経営者になり気付いた「多くの意見」に潜む罠。24歳のリーダーが捨てた常識 より
                    </cite>
                  <!-- /.o-label__source --></div>
                <!-- /.l-unit__feedback footer --></div>
              <!-- /.l-unit__feedback --></div>
            </li>

            <li class="o-list__feedback--item">
              <div class="l-unit__feedback">
                <div class="l-unit__feedback--header">
                  <div class="o-label__profile">
                    <p class="o-label__profile--item">
                      宅配の地域配送業/ 執行役員
                    </p>
                  <!-- /.o-label__profile --></div>
                <!-- /.l-unit__feedback header --></div>

                <div class="l-unit__feedback--image">
                  <img
                    class="img"
                    src="@/images/about/pic-feedback_03.svg"
                    loading="lazy"
                    alt="">
                <!-- /.l-unit__feedback image --></div>

                <div class="l-unit__feedback--description">
                  <p class="p">
                    この記事を読んで勇気をもらいました。<em class="em">私も物流業界で20年以上働いていますが、ドライバー不足問題に悩まされていました。</em>SNS活用や週休3日制導入など、『あなたの幸せを応援する企業』を掲げてブランディングを始めています。
                  </p>
                  <p class="p">
                    <em class="em">信念を貫き通す勇気と覚悟を持って経営していきます。</em>
                  </p>
                <!-- /.l-unit__feedback description --></div>

                <div class="l-unit__feedback--footer">
                  <div class="o-label__source">
                    <cite class="o-label__source--item">
                      教育で人は変わらない。社長の信念が証明した「人が辞めない組織」の作り方 より
                    </cite>
                  <!-- /.o-label__source --></div>
                <!-- /.l-unit__feedback footer --></div>
              <!-- /.l-unit__feedback --></div>
            </li>

            <li class="o-list__feedback--item">
              <div class="l-unit__feedback">
                <div class="l-unit__feedback--header">
                  <div class="o-label__profile">
                    <p class="o-label__profile--item">
                      設備工事業 / 代表取締役
                    </p>
                  <!-- /.o-label__profile --></div>
                <!-- /.l-unit__feedback header --></div>

                <div class="l-unit__feedback--image">
                  <img
                    class="img"
                    src="@/images/about/pic-feedback_04.svg"
                    loading="lazy"
                    alt="">
                <!-- /.l-unit__feedback image --></div>

                <div class="l-unit__feedback--description">
                  <p class="p">
                    4年前に会社を事業承継してから社内改革を進めました。『来客時に立礼を必ずしましょう』『遅刻の時は上長に必ず連絡をしましょう』など、曖昧だったことを明確にしました。25名から16名に減りましたが、売上・経常利益ともに当時を上回り、<em class="em">痛みを伴った改革が成果を上げています。教育への投資を諦めたくないと思います。</em>
                  </p>
                <!-- /.l-unit__feedback description --></div>

                <div class="l-unit__feedback--footer">
                  <div class="o-label__source">
                    <cite class="o-label__source--item">
                      教育で人は変わらない。社長の信念が証明した「人が辞めない組織」の作り方 より
                    </cite>
                  <!-- /.o-label__source --></div>
                <!-- /.l-unit__feedback footer --></div>
              <!-- /.l-unit__feedback --></div>
            </li>

            <li class="o-list__feedback--item">
              <div class="l-unit__feedback">
                <div class="l-unit__feedback--header">
                  <div class="o-label__profile">
                    <p class="o-label__profile--item">
                      店舗・施設の内装設計施工業 / 取締役会長
                    </p>
                  <!-- /.o-label__profile --></div>
                <!-- /.l-unit__feedback header --></div>

                <div class="l-unit__feedback--image">
                  <img
                    class="img"
                    src="@/images/about/pic-feedback_05.svg"
                    loading="lazy"
                    alt="">
                <!-- /.l-unit__feedback image --></div>

                <div class="l-unit__feedback--description">
                  <p class="p">
                    <em class="em">最近、3年目の社員が退職してしまいました。弊社では離職率ゼロを目指して取り組んできましたが、この記事を読んで『互いを思いやる気持ちが足りなかった』と反省しました。</em>
                  </p>
                  <p class="p">
                    それぞれの立場で部下を思いやる、上司を思いやる、お客様を思いやる。自分の存在をはっきり示し、共に協力していくことの大切さを教えてもらいました。
                  </p>
                <!-- /.l-unit__feedback description --></div>

                <div class="l-unit__feedback--footer">
                  <div class="o-label__source">
                    <cite class="o-label__source--item">
                      『この会社は変わらない』と諦めていた組織を変えたリーダーの信念。地方中小、100億企業に至る18年史 より
                    </cite>
                  <!-- /.o-label__source --></div>
                <!-- /.l-unit__feedback footer --></div>
              <!-- /.l-unit__feedback --></div>
            </li>
          </ul>
        <!-- /.o-list__feedback --></div>
      <!-- /.f-section__about body --></div>
    <!-- /.f-section__about --></section>

    <section class="f-section__about is-cta">
      <div class="f-section__about--body is-contents">
        <div class="o-heading__cta">
          <h2 class="o-heading__cta--item">
            まずは無料で、あなたの経営課題のヒントを
          </h2>
        <!-- /.o-heading__cta --></div>

        <p
          class="p u-text__white u-text__center"
          data-align-sp="left">
          登録すると、すべてのコンテンツがすぐに閲覧可能。<br>
          クレジットカード登録は不要です。
        </p>

        <p
          class="p u-text__white u-text__center"
          data-align-sp="left">
          経営課題の解決に、ひとりで悩む必要はありません。<br>
          20万人以上の経営者が集う場から、解決の糸口を見つけてください。
        </p>

        <ButtonLpCta
          class="u-block__max560"
          :href="newUserRegistrationPath({ trcd: `${trackingCodePrefix}_2` })"
          font-size="xLarge"
          size="normal"
          animate>
          <span
            class="span"
            v-html="ctaButtonLabel" />
        </ButtonLpCta>
      <!-- /.f-section__about body --></div>
    <!-- /.f-section__about --></section>
  </div>
</template>

<style lang="sass" scoped>
$contents-width-pc: 1240
$base-font-color: #2c3e50

// override
.p
  color: $base-font-color
  font-size: $font-x-large

  +breakpoint($max-media: tablet)
    font-size: $font-normal

.u-text
  &__white
    color: $font-white

// foundation
.f-section__about
  $root: &
  // is-background の位置基準
  position: relative
  padding: 0 #{$global-prop-pc}px

  & &--body
    &.is-contents
      width: 100%
      max-width: #{$contents-width-pc}px
      // is-background より後ろに描画して前面に出す
      position: relative
      margin: 0 auto
      padding: changeSizePx2Em(80) 0

    &.is-background
      width: 100%
      height: 100%
      position: absolute
      top: 0
      left: 0

  &[data-point="1"]
    background-color: rgba($bg-white, 0.8)

    #{$root}--body
      &.is-contents
        max-width: 1000px

  &[data-point="2"],
  &[data-point="4"]
    background-image: linear-gradient(to bottom, #f8fbff 0%,#f2f7fc 100%)

  &[data-point="3"]
    background-color: rgba($bg-white, 1)

  &.is-cta
    background-image: linear-gradient(to right, #2F5B7C 0%, #1A3A52 50%, #2F5B7C 100%)

  +breakpoint($max-media: tablet)
    padding: 0 #{$global-prop-sp}px

    #{$root}--body
      &.is-contents
        padding: changeSizePx2Em(32) 0

// layout
.l-unit__feature
  display: flex
  flex-direction: column

  & &--header
    margin-bottom: 0.5em

  & &--image
    order: -1
    margin-bottom: 1.3em

.l-unit__feedback
  $root: &
  display: grid
  grid-template-columns: 70px 1fr
  grid-template-rows: 1fr auto auto 1fr auto
  align-items: center
  column-gap: 0.7em

  & &--header
    grid-column: 2 / 3
    grid-row: 2 / 3

  & &--image
    grid-column: 1 / 2
    grid-row: 1 / 5

  & &--description
    grid-column: 1 / 3
    grid-row: 5 / 6
    margin-top: 0.5em

  & &--footer
    grid-column: 2 / 3
    grid-row: 3 / 4

  +breakpoint($max-media: tablet)
    display: flex
    flex-direction: column
    align-items: center

    #{$root}--image
      order: -1
      margin-bottom: 0.5em

    #{$root}--description
      order: 1

.l-cols__feature
  $root: &
  display: grid
  grid-template-columns: repeat(2, 1fr)
  gap: 2.5em

  +breakpoint($max-media: tablet)
    display: block

    #{$root}--col
      margin-top: 2em

      &:first-child
        margin-top: 0

// object
.o-figure__background
  width: 100%
  height: 100%

  & &--item
    width: 100%
    height: 100%
    object-fit: cover
    // 提供画像が濃いため薄く表示する。背景画像だった頃は alpha 0.8 の白レイヤーを
    // 重ねて擬似的に薄くしていたので、その裏返しで 0.2 とする
    opacity: 0.2

.o-heading__section
  $root: &
  margin: 0 auto changeSizePx2Em(40)

  & &--item
    line-height: 1.5
    color: #1a3a52
    font:
      size: 4.8rem
      weight: bold
    text-align: center

  +breakpoint($max-media: tablet)
    margin-bottom: 2em

    #{$root}--item
      font-size: $font-huge

.o-heading__feature
  $root: &

  & &--item
    font-weight: bold
    text-align: center

    &.is-primary
      color: $font-vermilion
      font-size: $font-xx-huge
      margin-bottom: 0.3em

      &:last-child
        margin-bottom: 0

    &.is-secondary
      color: $base-font-color
      font-size: $font-huge

  +breakpoint($max-media: tablet)
    #{$root}--item
      text-align: left

      &.is-primary
        font-size: $font-huge

      &.is-secondary
        font-size: $font-x-large

.o-heading__cta
  $root: &
  margin-bottom: changeSizePx2Em(40)

  & &--item
    color: $font-white
    font:
      size: $font-xx-huge
      weight: bold
    text-align: center

  +breakpoint($max-media: tablet)
    display: flex
    flex-direction: column
    align-items: center

    #{$root}--item
      font-size: 2.8rem
      text-align: left

.o-list__achievement
  $root: &
  display: grid
  grid-template-columns: repeat(3, 1fr)
  column-gap: 1em
  margin-top: 2em

  &:first-child
    margin-top: 0

  & &--unit
    padding: changeSizePx2Em(20)
    background-color: rgba($bg-white, 1)
    border-radius: 10px
    box-shadow: 0 4px 10px 0 rgba($bg-black, 0.1)

  & &--term
    color: $base-font-color
    font:
      size: $font-x-large
      weight: bold
    text-align: center
    margin-bottom: 1em

    &:last-child
      margin-bottom: 0

  & &--description
    margin-top: 1em

    &:first-child
      margin-top: 0

  +breakpoint($max-media: tablet)
    display: block

    #{$root}--unit
      margin-top: 1em

      &:first-child
        margin-top: 0

    #{$root}--term
      margin-bottom: 0.7em

    #{$root}--description
      margin-top: 0.5em

.o-list__feedback
  max-width: 1000px
  margin: 0 auto

  & &--item
    margin-top: 2em
    padding: changeSizePx2Em(20) changeSizePx2Em(40)
    background-color: rgba($bg-white, 1)
    border-radius: 10px
    box-shadow: 0 4px 10px 0 rgba($bg-black, 0.1)

    &:first-child
      margin-top: 0

.o-label__achievement
  $root: &

  & &--item
    line-height: 1
    color: $base-font-color
    text-align: center
    margin-bottom: 0.8em

    &:last-child
      margin-bottom: 0

    &.is-primary
      font:
        size: 3rem
        weight: bold

      > .strong
        color: $font-vermilion
        font:
          size: 3.13em // ≒ 94px
          family: Helvetica, Arial, sans-serif

    &.is-secondary
      font-size: $font-x-large

  +breakpoint($max-media: tablet)
    #{$root}--item
      &.is-primary
        font-size: $font-normal

        > .strong
          font-size: 2.5em  // ≒ 40px

      &.is-secondary
        font-size: $font-normal

.o-label__profile
  & &--item
    color: $base-font-color
    font:
      size: $font-x-large
      weight: bold

.o-label__source
  & &--item
    color: #a5a5a5
    font:
      size: $font-x-small
      style: italic

    &::before
      content: '―'
      margin-right: 0.1em
</style>
